import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import { isDebugPanelEnabled } from '../debug/isDebugPanelEnabled';
import { useDebugLogContextOptional } from '../debug';
import type { ChatMessage } from '../types/messages';
import type { UserInfo } from '../types/user';
import { getTripStatusLabel, TRIP_STATUS, TRIP_STATUS_LABELS } from '../types/tripStatus';
import type { ProposalBundle } from '../api/proposalBundles';
import type { ReservationInfo } from '../api/reservationInfo';
import MessageSection from './message_section/MessageSection';
import type { FormValues } from './message_section/ReservationInfoFormTypes';
import { useAiMessages, type ProposalNotification } from '../hooks/useAiMessages';

interface Props {
  tripId: string;
  trip: TripResponseArgs | null;
  user?: UserInfo;
  pusherKey: string;
  pusherChannelPrefix: string;
  headerTitle?: string;
  subtitle?: string;
  children?: React.ReactNode;
  enableMessaging?: boolean;
  initialMessages?: ChatMessage[];
  onProposalReceived?: (notification: ProposalNotification) => void;
  onReservationFormReceived: () => void;
  proposalBundles?: ProposalBundle[];
  onSelectItem?: (bundleId: number, itemId: number) => void;
  onCloseItem?: (bundleId: number) => void;
  reservationInfo?: ReservationInfo | null;
  onSubmitReservationInfo?: (values: FormValues) => Promise<boolean>;
  isSubmittingReservationInfo?: boolean;
  onArrangementRequestSuccess?: () => Promise<void>;
  onReservationInfoFormEditableChange: (editable: boolean) => void;
}

const ChatArea = ({
  tripId,
  trip,
  user,
  pusherKey,
  pusherChannelPrefix,
  headerTitle,
  subtitle,
  children,
  enableMessaging = true,
  initialMessages = [],
  onProposalReceived,
  onReservationFormReceived,
  proposalBundles = [],
  onSelectItem,
  onCloseItem,
  reservationInfo,
  onSubmitReservationInfo,
  isSubmittingReservationInfo,
  onArrangementRequestSuccess,
  onReservationInfoFormEditableChange
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const debugContext = useDebugLogContextOptional();

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  }, []);
  const {
    messages,
    inputValue,
    sending,
    canSend,
    handleInputChange,
    handleSend,
    disableReservationInfoForm,
    handleArrangementRequest,
    isRequestingArrangement,
    isArrangementRequested,
    inputDisabled,
    proposalChangeDisabled,
    isReservationInfoFormEditable
  } = useAiMessages({
    tripId,
    pusherKey,
    pusherChannelPrefix,
    enabled: enableMessaging,
    initialMessages,
    onProposalReceived,
    onReservationFormReceived,
    onScrollToBottom: scrollToBottom
  });

  useEffect(() => {
    onReservationInfoFormEditableChange(isReservationInfoFormEditable);
  }, [isReservationInfoFormEditable, onReservationInfoFormEditableChange]);

  const handleArrangementRequestWithRefetch = useCallback(async () => {
    const success = await handleArrangementRequest();
    if (success && onArrangementRequestSuccess) {
      await onArrangementRequestSuccess();
    }
  }, [handleArrangementRequest, onArrangementRequestSuccess]);

  const handleSubmitReservationInfo = useCallback(
    async (values: FormValues) => {
      if (!onSubmitReservationInfo) return;
      const success = await onSubmitReservationInfo(values);
      if (success) {
        disableReservationInfoForm();
      }
    },
    [onSubmitReservationInfo, disableReservationInfoForm]
  );

  const initialBundlesLoadedRef = useRef(false);
  const prevTripIdRef = useRef(tripId);
  useEffect(() => {
    if (prevTripIdRef.current !== tripId) {
      initialBundlesLoadedRef.current = false;
      prevTripIdRef.current = tripId;
    }
  }, [tripId]);

  // NOTE: proposalBundlesがない会話ではスクロール不要（会話量が少ないため）
  // tripId変更時の古いproposalBundlesによる誤発火は、親コンポーネントでの非同期更新順序により実際には発生しない
  useEffect(() => {
    if (proposalBundles.length > 0 && !initialBundlesLoadedRef.current) {
      initialBundlesLoadedRef.current = true;
      scrollToBottom();
    }
  }, [proposalBundles, scrollToBottom]);

  const headerStatus = useMemo(() => {
    if (isArrangementRequested) {
      return TRIP_STATUS_LABELS[TRIP_STATUS.ARRANGING];
    }
    return getTripStatusLabel(trip?.status, trip?.draft);
  }, [isArrangementRequested, trip?.status, trip?.draft]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>{headerTitle || `旅程番号：${tripId}`}</div>
          <div style={styles.subtitle}>{subtitle || headerStatus}</div>
        </div>
        <div style={styles.headerButtons}>
          {isDebugPanelEnabled() && debugContext && (
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => debugContext.setIsOpen(!debugContext.isOpen)}
            >
              エラーテスト: {debugContext.isOpen ? 'ON' : 'OFF'}
            </button>
          )}
          <span style={styles.badge}>{headerStatus}</span>
        </div>
      </div>
      <MessageSection
        trip={trip}
        user={user}
        messages={messages}
        proposalBundles={proposalBundles}
        onSelectItem={onSelectItem}
        onCloseItem={onCloseItem}
        reservationInfo={reservationInfo}
        onSubmitReservationInfo={handleSubmitReservationInfo}
        isSubmittingReservationInfo={isSubmittingReservationInfo}
        onArrangementRequest={handleArrangementRequestWithRefetch}
        isRequestingArrangement={isRequestingArrangement}
        isArrangementRequested={isArrangementRequested}
        proposalChangeDisabled={proposalChangeDisabled}
        scrollContainerRef={scrollContainerRef}
      />
      {children || (
        <div style={styles.inputArea}>
          <textarea
            placeholder="メッセージを入力して下さい"
            style={{
              ...styles.textarea,
              ...(inputDisabled ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {})
            }}
            rows={2}
            value={inputValue}
            onChange={event => handleInputChange(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                handleSend();
              }
            }}
            disabled={inputDisabled}
          />
          <button
            type="button"
            style={{
              ...styles.primaryButton,
              opacity: canSend ? 1 : 0.5,
              cursor: canSend ? 'pointer' : 'not-allowed'
            }}
            onClick={handleSend}
            disabled={!canSend}
          >
            {sending ? '送信中...' : '送信'}
          </button>
        </div>
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    borderRight: `1px solid ${theme.grayBorderColor}`,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden' as const
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: '#ffffff'
  },
  title: {
    fontSize: '16px',
    fontWeight: 600 as const,
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '12px',
    color: theme.grayTextColor
  },
  headerButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  badge: {
    backgroundColor: '#e6f4fb',
    color: theme.linkColor,
    padding: '6px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    border: `1px solid ${theme.linkColor}`
  },
  inputArea: {
    display: 'flex',
    alignItems: 'center' as const,
    padding: '16px',
    gap: '12px',
    borderTop: `1px solid ${theme.grayBorderColor}`
  },
  textarea: {
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${theme.grayBorderColor}`,
    resize: 'none' as const,
    fontFamily: 'inherit'
  },
  primaryButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '16px 48px',
    fontWeight: 600 as const,
    cursor: 'pointer'
  },
  secondaryButton: {
    backgroundColor: theme.contentBgColor,
    color: theme.grayTextColor,
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer'
  }
});

export default ChatArea;
